/**
 * Data-plane wire contract (the `/v1` API that tenant apps consume).
 *
 * Design rules these schemas enforce:
 *  - Counters are always integers. The UI formats them with `formatCount`.
 *  - Per-user state never lives on the content DTO; it hangs off `viewer`,
 *    so the same title payload is cacheable across users.
 *  - `videoUrl` is nullable and is ONLY populated by POST /v1/playback after
 *    an entitlement check. Catalog responses always return null for it.
 */

import { z } from 'zod';
import {
  USER_ACTION,
  WALLET_REASON,
  WATCH_EVENT_TYPE,
} from './enums';

/**
 * Boolean query param. `z.coerce.boolean()` would turn the string `"false"`
 * into `true`, so parse the literal text instead.
 */
const booleanQuery = (defaultValue: boolean) =>
  z
    .enum(['true', 'false', '1', '0'])
    .optional()
    .transform((v) => (v === undefined ? defaultValue : v === 'true' || v === '1'));

/**
 * UUID identifier. Validated at the edge so a malformed id becomes a 400 with a
 * useful message rather than a Postgres cast error surfaced as a 500.
 */
export const IdSchema = z.uuid();

// ---------------------------------------------------------------------------
// Envelopes
// ---------------------------------------------------------------------------

/** RFC-7807-flavoured error body returned by every failed route. */
export const ProblemDto = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  /** Machine-readable code the client can branch on, e.g. `insufficient_coins`. */
  code: z.string().optional(),
  /** Field-level validation failures. */
  errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type Problem = z.infer<typeof ProblemDto>;

export const CursorPageMeta = z.object({
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  limit: z.number().int(),
});
export type CursorPageMetaType = z.infer<typeof CursorPageMeta>;

export function cursorPage<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), meta: CursorPageMeta });
}

export const CursorQuery = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(24),
});
export type CursorQueryType = z.infer<typeof CursorQuery>;

/**
 * Client-generated idempotency key. Required on every money-moving or
 * entitlement-granting request so a retried call can never double-spend.
 */
export const IdempotencyKey = z
  .string()
  .min(8)
  .max(128)
  .regex(/^[A-Za-z0-9_.:-]+$/, 'idempotency key may only contain A-Z a-z 0-9 and _ . : -');

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const StatsDto = z.object({
  views: z.number().int().nonnegative(),
  likes: z.number().int().nonnegative(),
  comments: z.number().int().nonnegative(),
  shares: z.number().int().nonnegative(),
  saves: z.number().int().nonnegative(),
});
export type Stats = z.infer<typeof StatsDto>;

export const ProgressDto = z.object({
  episodeId: z.string(),
  episodeNumber: z.number().int(),
  positionSeconds: z.number().int().nonnegative(),
  durationSeconds: z.number().int().nonnegative(),
  /** 0-100, floored. Directly consumable by the app's progress bar. */
  percent: z.number().min(0).max(100),
  updatedAt: z.string(),
});
export type Progress = z.infer<typeof ProgressDto>;

export const EpisodeDto = z.object({
  id: z.string(),
  episodeNumber: z.number().int(),
  title: z.string(),
  durationSeconds: z.number().int().nonnegative(),
  isFree: z.boolean(),
  /** Resolved for the requesting user from `entitlements`. */
  isUnlocked: z.boolean(),
  coinPrice: z.number().int().nonnegative(),
  thumbnailUrl: z.string().nullable(),
  viewsCount: z.number().int().nonnegative(),
  /**
   * Always null here. Obtain a playable URL from POST /v1/playback, which
   * returns a short-TTL signed Bunny Stream URL after checking entitlement.
   */
  videoUrl: z.string().nullable(),
});
export type Episode = z.infer<typeof EpisodeDto>;

export const SeasonDto = z.object({
  seasonNumber: z.number().int(),
  title: z.string(),
  episodes: z.array(EpisodeDto),
});
export type Season = z.infer<typeof SeasonDto>;

/** Per-user overlay on a title. Absent for unauthenticated requests. */
export const ViewerStateDto = z.object({
  isLiked: z.boolean(),
  isBookmarked: z.boolean(),
  isSaved: z.boolean(),
  progress: ProgressDto.nullable(),
});
export type ViewerState = z.infer<typeof ViewerStateDto>;

export const TitleSummaryDto = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  subTitle: z.string().nullable(),
  genres: z.array(z.string()),
  tags: z.array(z.string()),
  synopsis: z.string(),
  posterVerticalUrl: z.string().nullable(),
  posterHorizontalUrl: z.string().nullable(),
  heroBackdropUrl: z.string().nullable(),
  /** 0-10, one decimal, e.g. 9.4 */
  rating: z.number().min(0).max(10),
  totalEpisodes: z.number().int().nonnegative(),
  director: z.string().nullable(),
  cast: z.array(z.string()),
  themeColor: z.string().nullable(),
  featured: z.boolean(),
  stats: StatsDto,
  viewer: ViewerStateDto.nullable(),
});
export type TitleSummary = z.infer<typeof TitleSummaryDto>;

/** Full detail payload, including seasons/episodes. */
export const TitleDetailDto = TitleSummaryDto.extend({
  seasons: z.array(SeasonDto),
});
export type TitleDetail = z.infer<typeof TitleDetailDto>;

export const ContinueWatchingDto = z.object({
  title: TitleSummaryDto,
  episodeId: z.string(),
  episodeNumber: z.number().int(),
  totalEpisodes: z.number().int(),
  positionSeconds: z.number().int().nonnegative(),
  progressPercent: z.number().min(0).max(100),
  lastWatchedAt: z.string(),
});
export type ContinueWatching = z.infer<typeof ContinueWatchingDto>;

export const CategoryDto = z.object({
  slug: z.string(),
  label: z.string(),
  /** True for entries that should appear in the horizontal category bar. */
  isNavCategory: z.boolean(),
  displayOrder: z.number().int(),
});
export type Category = z.infer<typeof CategoryDto>;

export const HomeRowKind = z.enum([
  'top_ranked',
  'premium',
  'trending',
  'new_releases',
  'genre',
  'because_you_watched',
]);
export type HomeRowKindType = z.infer<typeof HomeRowKind>;

export const HomeRowDto = z.object({
  id: z.string(),
  kind: HomeRowKind,
  title: z.string(),
  /** Present on `genre` rows so the client can deep-link into the full list. */
  genreSlug: z.string().nullable(),
  titles: z.array(TitleSummaryDto),
});
export type HomeRow = z.infer<typeof HomeRowDto>;

/** One round trip to render HomeScreen. */
export const HomeDto = z.object({
  categories: z.array(CategoryDto),
  hero: z.array(TitleSummaryDto),
  continueWatching: z.array(ContinueWatchingDto),
  rows: z.array(HomeRowDto),
  wallet: z.lazy(() => WalletDto).nullable(),
  dailyReward: z.lazy(() => DailyRewardStateDto).nullable(),
});
export type Home = z.infer<typeof HomeDto>;

// ---------------------------------------------------------------------------
// Playback
// ---------------------------------------------------------------------------

export const PlaybackRequest = z.object({
  titleId: IdSchema,
  episodeId: IdSchema,
  /** Lets the client resume without a second round trip. */
  resumePositionSeconds: z.number().int().nonnegative().optional(),
});

export const PlaybackDto = z.object({
  episodeId: z.string(),
  /** Short-TTL signed Bunny Stream HLS URL. Never cache or persist this. */
  playbackUrl: z.string(),
  posterUrl: z.string().nullable(),
  /** Seconds until the URL stops working; refresh before it hits zero. */
  expiresInSeconds: z.number().int().positive(),
  hlsUrl: z.string().nullable(),
  mp4FallbackUrl: z.string().nullable(),
  resumePositionSeconds: z.number().int().nonnegative(),
  durationSeconds: z.number().int().nonnegative(),
  subtitles: z.array(z.object({ language: z.string(), url: z.string(), label: z.string() })),
});
export type Playback = z.infer<typeof PlaybackDto>;

// ---------------------------------------------------------------------------
// Wallet, purchases, rewards
// ---------------------------------------------------------------------------

export const WalletDto = z.object({
  coins: z.number().int().nonnegative(),
  gems: z.number().int().nonnegative(),
  streakDays: z.number().int().nonnegative(),
  lastClaimDate: z.string().nullable(),
});
export type Wallet = z.infer<typeof WalletDto>;

export const WalletTransactionDto = z.object({
  id: z.string(),
  deltaCoins: z.number().int(),
  deltaGems: z.number().int(),
  reason: z.enum(WALLET_REASON),
  /** Opaque pointer back to the originating row (purchase, entitlement, claim). */
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  balanceCoinsAfter: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type WalletTransaction = z.infer<typeof WalletTransactionDto>;

export const DailyRewardDto = z.object({
  day: z.number().int().min(1),
  coins: z.number().int().nonnegative(),
  gems: z.number().int().nonnegative().optional(),
  claimed: z.boolean(),
  isToday: z.boolean(),
});
export type DailyReward = z.infer<typeof DailyRewardDto>;

export const DailyRewardStateDto = z.object({
  streakDays: z.number().int().nonnegative(),
  /** False once today's reward is claimed — the UI disables the button. */
  canClaimToday: z.boolean(),
  todayCoins: z.number().int().nonnegative(),
  todayGems: z.number().int().nonnegative(),
  days: z.array(DailyRewardDto),
});
export type DailyRewardState = z.infer<typeof DailyRewardStateDto>;

export const CoinPackageDto = z.object({
  id: z.string(),
  sku: z.string(),
  coins: z.number().int().positive(),
  bonusCoins: z.number().int().nonnegative(),
  priceMinor: z.number().int().nonnegative(),
  currency: z.string().length(3),
  revenuecatProductId: z.string().nullable(),
});
export type CoinPackage = z.infer<typeof CoinPackageDto>;

/**
 * Step 1 of a purchase: the client declares intent, we persist a `pending`
 * row keyed on a client idempotency key. Step 2 is the RevenueCat webhook,
 * which flips it to `completed` and credits the ledger exactly once.
 */
export const PurchaseIntentRequest = z.object({
  packageId: z.string(),
  idempotencyKey: IdempotencyKey,
});

export const PurchaseIntentDto = z.object({
  purchaseId: z.string(),
  status: z.enum(['pending', 'completed', 'failed', 'refunded']),
  revenuecatProductId: z.string().nullable(),
  coinsOnCompletion: z.number().int().nonnegative(),
});
export type PurchaseIntent = z.infer<typeof PurchaseIntentDto>;

// ---------------------------------------------------------------------------
// Unlocking
// ---------------------------------------------------------------------------

export const UnlockEpisodeRequest = z.object({
  idempotencyKey: IdempotencyKey,
});

export const UnlockEpisodeDto = z.object({
  unlocked: z.boolean(),
  /** False when the episode was already unlocked — no coins were taken. */
  alreadyOwned: z.boolean(),
  coinsSpent: z.number().int().nonnegative(),
  wallet: WalletDto,
  entitlementId: z.string(),
});
export type UnlockEpisode = z.infer<typeof UnlockEpisodeDto>;

// ---------------------------------------------------------------------------
// Watch events & progress
// ---------------------------------------------------------------------------

export const WatchEventDto = z.object({
  /** Client-generated ULID/UUID grouping events from one playback session. */
  sessionId: z.string().min(1).max(64),
  titleId: IdSchema,
  episodeId: IdSchema,
  type: z.enum(WATCH_EVENT_TYPE),
  positionSeconds: z.number().int().nonnegative(),
  durationSeconds: z.number().int().nonnegative(),
  /** 0-100 at the moment of the event. */
  percent: z.number().min(0).max(100),
  /** Client clock; the server records its own received_at as authoritative. */
  occurredAt: z.string().optional(),
});

export const WatchEventsRequest = z.object({
  events: z.array(WatchEventDto).min(1).max(50),
});

export const WatchEventsAckDto = z.object({
  accepted: z.number().int().nonnegative(),
  /** Events dropped as stale duplicates; the client should not resend them. */
  rejected: z.number().int().nonnegative(),
});
export type WatchEventsAck = z.infer<typeof WatchEventsAckDto>;

export const ProgressRequest = z.object({
  titleId: IdSchema,
  episodeId: IdSchema,
  positionSeconds: z.number().int().nonnegative(),
  durationSeconds: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// Library & social
// ---------------------------------------------------------------------------

export const LibraryKind = z.enum(['watchlist', 'liked', 'saved', 'history']);
export type LibraryKindType = z.infer<typeof LibraryKind>;

export const UserActionRequest = z.object({
  action: z.enum(USER_ACTION),
  titleId: IdSchema,
});

export const UserActionResultDto = z.object({
  action: z.enum(USER_ACTION),
  titleId: z.string(),
  /** The resulting state, so the client can reconcile optimistic UI. */
  active: z.boolean(),
  count: z.number().int().nonnegative(),
});
export type UserActionResult = z.infer<typeof UserActionResultDto>;

export const CommentDto = z.object({
  id: z.string(),
  titleId: z.string(),
  episodeId: z.string().nullable(),
  authorName: z.string(),
  authorAvatarUrl: z.string().nullable(),
  content: z.string(),
  likesCount: z.number().int().nonnegative(),
  isLikedByViewer: z.boolean(),
  parentCommentId: z.string().nullable(),
  createdAt: z.string(),
});
export type Comment = z.infer<typeof CommentDto>;

export const CreateCommentRequest = z.object({
  content: z.string().trim().min(1).max(2000),
  episodeId: IdSchema.nullish(),
  parentCommentId: IdSchema.nullish(),
});

// ---------------------------------------------------------------------------
// Search & feed
// ---------------------------------------------------------------------------

export const TitleListQuery = CursorQuery.extend({
  q: z.string().trim().max(120).optional(),
  genre: z.string().trim().max(64).optional(),
  tag: z.string().trim().max(64).optional(),
  sort: z.enum(['relevance', 'popular', 'rating', 'newest']).default('relevance'),
});
export type TitleListQueryType = z.infer<typeof TitleListQuery>;

export const SimilarTitlesQuery = z.object({
  limit: z.coerce.number().int().min(1).max(24).default(8),
  /** Include the reason so the UI can say "Because you watched X". */
  withReason: booleanQuery(false),
});

export const SimilarTitleDto = z.object({
  title: TitleSummaryDto,
  /** 0-1, higher is more similar. */
  score: z.number().min(0).max(1),
  /** Which signal produced the match, for debugging and UI copy. */
  matchedBy: z.enum(['embedding', 'genre', 'tag', 'cast', 'blended']),
  reason: z.string().nullable(),
});
export type SimilarTitle = z.infer<typeof SimilarTitleDto>;

export const ForYouFeedQuery = CursorQuery.extend({
  /** Exclude titles the user already finished or bookmarked. */
  excludeSeen: booleanQuery(true),
});

export const ForYouFeedItemDto = z.object({
  title: TitleSummaryDto,
  /** Episode the reel should start on (resume point, or 1). */
  startEpisodeId: z.string(),
  startEpisodeNumber: z.number().int(),
  score: z.number().min(0).max(1),
  reason: z.string().nullable(),
});
export type ForYouFeedItem = z.infer<typeof ForYouFeedItemDto>;

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const SessionDto = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int(),
  user: z.object({
    id: z.string(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    tenantId: z.string(),
    createdAt: z.string(),
  }),
  wallet: WalletDto,
});
export type Session = z.infer<typeof SessionDto>;
